import axios from 'axios';

// Use env var if provided (e.g. for direct dev access or prod domain), 
// otherwise default to empty string for relative paths (proxied by Nginx)
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 second timeout to prevent hanging requests
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('leroy_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle errors consistently
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        error.message = 'Request timed out. Please try again.';
      } else {
        error.message = 'Network error. Please check your internet connection.';
      }
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('leroy_token');
      // Let React Router handle navigation via ProtectedRoute
      // Don't force a hard page reload which causes infinite loop
    }
    
    if (error.response?.status === 429) {
      error.message = error.response.data?.detail || 'Too many requests. Please try again later.';
    }

    // an off-network refusal is a rule, not a fault — say which rule, or every
    // screen reports "insufficient permissions" and the person retries forever
    if (error.response?.status === 403
        && error.response.data?.detail === 'network_not_allowed') {
      error.message = 'This account can only be used from the office network.';
    }
    
    if (error.response?.status >= 500) {
      error.message = 'Server error. Please try again later.';
    }
    
    return Promise.reject(error);
  }
);

export default api;

// Stakeholder Tag API Functions

/**
 * Create a stakeholder tag for an order
 * @param {string} orderId - The order ID
 * @param {Object} tagData - Tag data containing user_id and optional role_note
 * @returns {Promise<Object>} The created stakeholder tag with populated user details
 */
export const createStakeholderTag = async (orderId, tagData) => {
  try {
    const response = await api.post(`/orders/${orderId}/stakeholders`, tagData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all stakeholder tags for an order
 * @param {string} orderId - The order ID
 * @returns {Promise<Array>} Array of stakeholder tags with populated user details
 */
export const getStakeholderTags = async (orderId) => {
  try {
    const response = await api.get(`/orders/${orderId}/stakeholders`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update a stakeholder tag's role note
 * @param {string} orderId - The order ID
 * @param {string} tagId - The stakeholder tag ID
 * @param {Object} updateData - Update data containing role_note
 * @returns {Promise<Object>} The updated stakeholder tag
 */
export const updateStakeholderTag = async (orderId, tagId, updateData) => {
  try {
    const response = await api.put(`/orders/${orderId}/stakeholders/${tagId}`, updateData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a stakeholder tag from an order
 * @param {string} orderId - The order ID
 * @param {string} tagId - The stakeholder tag ID
 * @returns {Promise<Object>} Success response
 */
export const deleteStakeholderTag = async (orderId, tagId) => {
  try {
    const response = await api.delete(`/orders/${orderId}/stakeholders/${tagId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== CATALOG API ====================

/**
 * Get catalog items with optional filters
 */
export const getCatalogItems = async (params = {}) => {
  const response = await api.get('/catalog/items', { params });
  return response.data;
};

/**
 * Get single catalog item by ID
 */
export const getCatalogItem = async (itemId) => {
  const response = await api.get(`/catalog/items/${itemId}`);
  return response.data;
};

/**
 * Create new catalog item
 */
export const createCatalogItem = async (data) => {
  const response = await api.post('/catalog/items', data);
  return response.data;
};

/**
 * Update catalog item
 */
export const updateCatalogItem = async (itemId, data) => {
  const response = await api.put(`/catalog/items/${itemId}`, data);
  return response.data;
};

/**
 * Delete catalog item
 */
export const deleteCatalogItem = async (itemId) => {
  const response = await api.delete(`/catalog/items/${itemId}`);
  return response.data;
};

/**
 * Add or update customer-specific pricing
 */
export const addCustomerPrice = async (itemId, data) => {
  const response = await api.post(`/catalog/items/${itemId}/customer-price`, data);
  return response.data;
};

/**
 * Remove customer-specific pricing
 */
export const removeCustomerPrice = async (itemId, customerId) => {
  const response = await api.delete(`/catalog/items/${itemId}/customer-price/${customerId}`);
  return response.data;
};

/**
 * Get all pricing for a specific customer
 */
export const getCustomerPricing = async (customerId) => {
  const response = await api.get(`/catalog/customer/${customerId}/pricing`);
  return response.data;
};

/**
 * Get low stock items
 */
export const getLowStockItems = async () => {
  const response = await api.get('/catalog/inventory/low-stock');
  return response.data;
};

/**
 * Manually adjust inventory stock
 */
export const adjustStock = async (data) => {
  const response = await api.post('/catalog/inventory/adjust', data);
  return response.data;
};

/**
 * Get stock transaction history
 */
export const getStockTransactions = async (params = {}) => {
  const response = await api.get('/catalog/inventory/transactions', { params });
  return response.data;
};


/**
 * Search colleagues by name for stakeholder tagging.
 *
 * Reads the directory, not the user list: the latter is account administration
 * and answers 403 to everyone but admin, receptionist and supervisor, so a
 * designer's picker came up empty. The shop-floor roles are excluded server
 * side.
 *
 * @param {string} query - Search query string
 * @param {number} limit - Maximum number of results (default: 10)
 * @returns {Promise<Object>} Object containing users array
 */
export const searchUsers = async (query, limit = 10) => {
  const response = await api.get('/users/directory', {
    params: { search: query || undefined, limit },
  });
  return response.data;
};

/**
 * Get customers list (for catalog item creation)
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Object containing customers array
 */
export const getCustomers = async (params = {}) => {
  const response = await api.get('/customers', { params });
  return response.data;
};

/**
 * Get product types/departments
 * @returns {Promise<Object>} Product types configuration
 */
export const getProductTypes = async () => {
  const response = await api.get('/product-types');
  return response.data;
};

// ==================== JOB CARD SUPERVISOR OVERRIDES ====================

/**
 * Skip the current stage and advance the batch/job card forward.
 * @param {string} jobCardId
 * @param {{ batch_id?: string, reason?: string }} body
 */
export const skipJobCardStage = async (jobCardId, body = {}) => {
  const response = await api.post(`/job-cards/${jobCardId}/skip-stage`, body);
  return response.data;
};

/**
 * Reroute a batch/job card to any stage in its pipeline.
 * @param {string} jobCardId
 * @param {{ batch_id?: string, target_stage_index?: number, target_stage_key?: string, reason?: string }} body
 */
export const moveJobCardToStage = async (jobCardId, body = {}) => {
  const response = await api.post(`/job-cards/${jobCardId}/move-to-stage`, body);
  return response.data;
};

/**
 * Add one process to a job that is already running. Insert-only: goods already
 * past that point of the line skip it rather than being fetched back for it.
 * @param {string} jobCardId
 * @param {{ stage_key: string, after_stage_key?: string|null, reason?: string }} body
 * @returns {Promise<Object>} { stages, will_run, already_past }
 */
export const insertJobCardStage = async (jobCardId, body) => {
  const response = await api.post(`/job-cards/${jobCardId}/stages/add`, body);
  return response.data;
};

/**
 * Remove a stage from the job card's pipeline.
 * @param {string} jobCardId
 * @param {{ stage_key: string }} body
 * @returns {Promise<Object>} { production_stages }
 */
export const removeJobCardStage = async (jobCardId, body) => {
  const response = await api.post(`/job-cards/${jobCardId}/remove-stage`, body);
  return response.data;
};

// ==================== BATCH PROCESSING ====================

/**
 * Plan one batch as several production runs. Nothing moves — the new batches
 * stand where the original stood.
 * @param {string} batchId
 * @param {{ quantities?: number[], count?: number, remarks?: string }} body
 * @returns {Promise<Object>} { batches }
 */
export const divideBatch = async (batchId, body) => {
  const response = await api.post(`/production/batches/${batchId}/divide`, body);
  return response.data;
};

/**
 * Close the current stage for several batches at once.
 * @param {{ batch_ids: string[], operator?: string, machine_used?: string, remarks?: string }} body
 * @returns {Promise<Object>} { message, advanced, failed }
 */
export const advanceBatches = async (body) => {
  const response = await api.post('/production/batches/advance', body);
  return response.data;
};
